import { useEffect, useMemo, useState } from "react";
import brandLogo from "../../../../assets/images/tnp-logo-neon.png";
import titleRankingIcon from "../../../../assets/images/hub-icons/cropped/title-ranking.png";
import AppLayout from "../../../../shared/components/AppLayout";
import SearchInput from "../../../../shared/components/SearchInput";
import UserAvatar from "../../../../shared/components/UserAvatar";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { OrganizerVisualIdentity } from "../../../../shared/utils/organizerVisualIdentity";
import { normalizeSearchText } from "../../../../shared/utils/searchText";
import {
  getPublicOrganizerVisualIdentity,
  listOrganizers,
  type OrganizerDirectoryItem,
} from "../../../auth/shared/services/authService";
import { getSuper8Ranking } from "../../shared/services/super8RankingService";
import type { Super8RankingResponse } from "../../shared/types/super8Ranking";
import "../../../../shared/styles/sourceHub.css";
import "./RankingPage.css";

type RankingOrganizer = OrganizerDirectoryItem & {
  rankingPlayers: number;
  rankingEvents: number;
  scoringLabel: string;
  identity: OrganizerVisualIdentity;
};

export function filterRankingOrganizers(
  organizers: RankingOrganizer[],
  search: string,
) {
  const query = normalizeSearchText(search);
  return query
    ? organizers.filter((organizer) =>
        normalizeSearchText(organizer.name).includes(query),
      )
    : organizers;
}

async function loadAllOrganizers() {
  const items: OrganizerDirectoryItem[] = [];
  let offset = 0;
  for (;;) {
    const response = await listOrganizers({ limit: 50, offset });
    items.push(...(response.items || []));
    if (response.nextOffset === null) break;
    offset = response.nextOffset;
  }
  return items;
}

function organizerIdentityFallback(
  organizer: OrganizerDirectoryItem,
): OrganizerVisualIdentity {
  return {
    mode:
      organizer.visualIdentityMode === "custom" ? "custom" : "tnp_standard",
    backgroundMode: organizer.backgroundMode || "blue",
    colors: {
      primaryColor: organizer.primaryColor || "",
      secondaryColor: organizer.secondaryColor || "",
      accentColor: organizer.accentColor || "",
    },
    logoUrl: organizer.logoUrl,
  };
}

function RankingStats({
  players,
  events,
  thirdValue,
  thirdLabel,
  playersLabel,
  eventsLabel,
}: {
  players: number;
  events: number;
  thirdValue: string | number;
  thirdLabel: string;
  playersLabel: string;
  eventsLabel: string;
}) {
  return (
    <div className="hub-landing-stats">
      <div className="hub-landing-stat">
        <span
          className="hub-landing-stat-icon material-symbols-outlined"
          aria-hidden="true"
        >
          group
        </span>
        <strong>{players}</strong>
        <small>{playersLabel}</small>
      </div>
      <div className="hub-landing-stat">
        <span
          className="hub-landing-stat-icon material-symbols-outlined"
          aria-hidden="true"
        >
          event
        </span>
        <strong>{events}</strong>
        <small>{eventsLabel}</small>
      </div>
      <div className="hub-landing-stat">
        <span
          className="hub-landing-stat-icon material-symbols-outlined"
          aria-hidden="true"
        >
          workspace_premium
        </span>
        <strong>{thirdValue}</strong>
        <small>{thirdLabel}</small>
      </div>
    </div>
  );
}

export default function RankingHubPage() {
  const [officialRanking, setOfficialRanking] =
    useState<Super8RankingResponse | null>(null);
  const [organizers, setOrganizers] = useState<RankingOrganizer[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [official, organizerSummary, directory] = await Promise.all([
          getSuper8Ranking({ source: "official", mode: "all" }),
          getSuper8Ranking({ source: "organizer", mode: "all" }),
          loadAllOrganizers(),
        ]);
        if (!active) return;
        const rankingOrganizerIds = new Set(
          (organizerSummary.availableOrganizers || [])
            .map((item) => Number(item.id || 0))
            .filter((id) => Number.isFinite(id) && id > 0),
        );
        const visibleDirectory = directory.filter((organizer) =>
          rankingOrganizerIds.has(Number(organizer.id)),
        );
        const loadedOrganizers = await Promise.all(
          visibleDirectory.map(async (organizer) => {
            const [ranking, identity] = await Promise.all([
              getSuper8Ranking({
                source: "organizer",
                mode: "all",
                organizerUserId: Number(organizer.id),
              }),
              getPublicOrganizerVisualIdentity(Number(organizer.id)).catch(() =>
                organizerIdentityFallback(organizer),
              ),
            ]);
            return {
              ...organizer,
              logoUrl:
                String(identity.logoUrl || "").trim() ||
                organizer.logoUrl ||
                null,
              rankingPlayers: Number(ranking.totals?.players || 0),
              rankingEvents: Number(ranking.totals?.events || 0),
              scoringLabel: String(ranking.scoringStandard?.label || "TNP"),
              identity,
            };
          }),
        );
        if (!active) return;
        setOfficialRanking(official);
        setOrganizers(
          loadedOrganizers.sort((a, b) =>
            a.name.localeCompare(b.name, "pt-BR"),
          ),
        );
        setError("");
      } catch (loadError) {
        if (!active) return;
        setError(
          loadError instanceof Error
            ? loadError.message
            : siteTexts.ranking.organizersDirectory.loadError,
        );
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!loading && !error && organizers.length === 0) {
      window.location.replace("#/ranking/tnp");
    }
  }, [error, loading, organizers.length]);

  const filteredOrganizers = useMemo(
    () => filterRankingOrganizers(organizers, search),
    [organizers, search],
  );
  const currentYear = new Date().getFullYear();

  return (
    <AppLayout title="" subtitle="">
      <main className="hub-landing organizer-showcase ranking-showcase">
        <div className="hub-landing-lines" aria-hidden="true" />
        <header className="hub-landing-header">
          <div
            className="hub-landing-icon hub-landing-icon--image"
            aria-hidden="true"
          >
            <img
              src={titleRankingIcon}
              alt=""
              className="hub-landing-icon-art hub-landing-icon-art--ranking"
            />
          </div>
          <h1>{siteTexts.ranking.hub.title}</h1>
          <p>{siteTexts.ranking.hub.subtitle}</p>
        </header>

        <section className="organizer-showcase-directory">
          <div className="organizer-showcase-toolbar">
            <SearchInput
              id="ranking-organizer-search"
              inputType="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onClear={() => setSearch("")}
              placeholder={
                siteTexts.ranking.organizersDirectory.searchExpandedPlaceholder
              }
              aria-label={siteTexts.ranking.organizersDirectory.searchAriaLabel}
            />
          </div>

          {error ? (
            <p className="source-hub-error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="hub-landing-grid organizer-showcase-grid">
            <section className="hub-landing-card hub-landing-card--official">
              <div className="hub-landing-card-logo-wrap">
                <img
                  src={brandLogo}
                  alt="TNP"
                  className="hub-landing-card-logo"
                />
              </div>
              <h2>{siteTexts.ranking.hub.officialCard.title}</h2>
              <p>{siteTexts.ranking.hub.officialCard.description}</p>
              <RankingStats
                players={Number(officialRanking?.totals?.players || 0)}
                events={Number(officialRanking?.totals?.events || 0)}
                thirdValue={currentYear}
                thirdLabel={siteTexts.ranking.hub.officialCard.stats.season}
                playersLabel={siteTexts.ranking.hub.officialCard.stats.players}
                eventsLabel={siteTexts.ranking.organizersDirectory.events}
              />
              <a
                className="hub-landing-cta hub-landing-cta--pink"
                href="#/ranking/tnp"
              >
                {siteTexts.ranking.hub.officialCard.cta}
              </a>
            </section>

            {loading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <span
                    className="hub-landing-card organizer-showcase-card--skeleton"
                    key={`ranking-organizer-skeleton-${index}`}
                  />
                ))
              : filteredOrganizers.map((organizer) => {
                  const logoUrl = String(
                    organizer.logoUrl || organizer.avatarUrl || "",
                  ).trim();
                  return (
                    <section
                      className="hub-landing-card hub-landing-card--organizers"
                      key={`ranking-organizer-${organizer.id}`}
                    >
                      <div className="hub-landing-card-logo-wrap organizer-showcase-logo">
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt=""
                            className="hub-landing-card-logo hub-landing-card-logo--organizer"
                          />
                        ) : (
                          <UserAvatar
                            name={organizer.name}
                            sex={organizer.sex || ""}
                            fallback="initials"
                            size={72}
                          />
                        )}
                      </div>
                      <h2>{organizer.name}</h2>
                      <p>{siteTexts.ranking.hub.organizersCard.description}</p>
                      <RankingStats
                        players={organizer.rankingPlayers}
                        events={organizer.rankingEvents}
                        thirdValue={organizer.scoringLabel}
                        thirdLabel={siteTexts.ranking.page.labels.scoringStandard}
                        playersLabel={siteTexts.ranking.organizersDirectory.athletes}
                        eventsLabel={siteTexts.ranking.organizersDirectory.events}
                      />
                      <a
                        className="hub-landing-cta hub-landing-cta--blue hub-landing-cta--choice"
                        href={`#/ranking/organizador/${organizer.id}`}
                      >
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          arrow_forward
                        </span>
                        {siteTexts.ranking.organizersDirectory.viewRanking}
                      </a>
                    </section>
                  );
                })}
          </div>
        </section>
      </main>
    </AppLayout>
  );
}
